/**
 * Soil depth along a sample's images, for the viewer's depth scale.
 *
 * The scanner sits in the soil tilted `tiltDeg` from the vertical, the top
 * edge of its shell `topEdgeDepthCm` below the surface. The image starts at the
 * top of its glass, `scannerTopEdgeCm` further down the scanner. A point
 * `d` cm down the image is then at
 *
 *     topEdgeDepthCm + cos(tilt) × (scannerTopEdgeCm + d)
 *
 * which is linear in d: the scale is a plain ruler, squeezed by cos(tilt).
 * Field estimates, good to a few cm.
 */
import type { SampleModel, ScannerModel } from '@/types/models';

export type DepthSetup = {
    topEdgeDepthCm: number;
    tiltDeg: number;
    scannerTopEdgeCm: number;
};

/** The sample's setup, once both it and its scanner are known. */
export function depthSetup(
    sample?: Pick<SampleModel, 'topEdgeDepthCm' | 'tiltDeg'> | null,
    scanner?: Pick<ScannerModel, 'topEdgeCm'> | null,
): DepthSetup | null {
    if (
        sample?.topEdgeDepthCm == null ||
        sample.tiltDeg == null ||
        scanner?.topEdgeCm == null
    )
        return null;
    return {
        topEdgeDepthCm: sample.topEdgeDepthCm,
        tiltDeg: sample.tiltDeg,
        scannerTopEdgeCm: scanner.topEdgeCm,
    };
}

/** The depth, in cm, of a point `distanceCm` down the image from its top. */
export function depthCmAt(setup: DepthSetup, distanceCm: number): number {
    const cos = Math.cos((setup.tiltDeg * Math.PI) / 180);
    return setup.topEdgeDepthCm + cos * (setup.scannerTopEdgeCm + distanceCm);
}
